import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { LogOut, Save, Loader2, Calendar as CalendarIcon, Clock, X, Plus, CalendarDays, Inbox, FileText, Mic, CalendarClock, BookOpen, Lock, Unlock, Ticket, Users, CalendarCheck, Star } from 'lucide-react';
import AdminBookings from './AdminBookings';
import AdminPacks from './AdminPacks';
import AdminReviews from './AdminReviews';
import AdminStudents from './AdminStudents';
import AdminMaterials from './AdminMaterials';
import AdminPodcast from './AdminPodcast';
import AdminCalendar from './AdminCalendar';
import AdminBlog from './AdminBlog';
import { Calendar as DayPickerCalendar } from '../components/ui/calendar';
import { es as esLocale } from 'date-fns/locale';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: '1', label: 'Lunes' },
  { key: '2', label: 'Martes' },
  { key: '3', label: 'Miércoles' },
  { key: '4', label: 'Jueves' },
  { key: '5', label: 'Viernes' },
  { key: '6', label: 'Sábado' },
  { key: '0', label: 'Domingo' },
];

// Possible time slots the teacher can toggle per day
const ALL_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00',
  '22:00', '23:00'
];

export default function AdminPanel() {
  const { admin, logout, token } = useAuth();
  const navigate = useNavigate();
  const [weekly, setWeekly] = useState({});
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]); // "YYYY-MM-DD HH:MM"
  const [openedSlots, setOpenedSlots] = useState([]);   // "YYYY-MM-DD HH:MM"
  const [bookedSlots, setBookedSlots] = useState({});    // {date: [time,...]} reservas activas
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [exceptionDates, setExceptionDates] = useState([]); // list of "YYYY-MM-DD" showing an exception row
  const [newExceptionDate, setNewExceptionDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('availability');

  const handleSessionExpired = async () => {
    toast.error('Sesión expirada. Inicia sesión de nuevo.');
    await logout();
    navigate('/admin/login');
  };

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const { data } = await axios.get(`${API}/availability`);
        const normalized = {};
        DAYS.forEach(({ key }) => {
          normalized[key] = data.weekly[key] || { enabled: false, slots: [] };
        });
        setWeekly(normalized);
        setBlockedDates(data.blocked_dates || []);
        // blocked_slots comes as map {date: [time,...]} - convert back to list
        const blockedSlotsList = [];
        Object.entries(data.blocked_slots || {}).forEach(([d, times]) => {
          (times || []).forEach((t) => blockedSlotsList.push(`${d} ${t}`));
        });
        setBlockedSlots(blockedSlotsList.sort());
        const openedSlotsList = [];
        Object.entries(data.opened_slots || {}).forEach(([d, times]) => {
          (times || []).forEach((t) => openedSlotsList.push(`${d} ${t}`));
        });
        setOpenedSlots(openedSlotsList.sort());
        setBookedSlots(data.booked_slots || {});
        // Any date with any exception gets an initial row
        const datesWithExceptions = new Set([
          ...Object.keys(data.blocked_slots || {}),
          ...Object.keys(data.opened_slots || {}),
        ]);
        setExceptionDates(Array.from(datesWithExceptions).sort());
      } catch (e) {
        toast.error('Error al cargar la disponibilidad');
      } finally {
        setLoading(false);
      }
    };
    fetchAvailability();
  }, []);

  const toggleDay = (dayKey) => {
    setWeekly((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], enabled: !prev[dayKey].enabled }
    }));
  };

  const toggleSlot = (dayKey, slot) => {
    setWeekly((prev) => {
      const slots = prev[dayKey].slots.includes(slot)
        ? prev[dayKey].slots.filter((s) => s !== slot)
        : [...prev[dayKey].slots, slot].sort();
      return { ...prev, [dayKey]: { ...prev[dayKey], slots } };
    });
  };

  const addBlockedDate = () => {
    if (newBlockedDate && !blockedDates.includes(newBlockedDate)) {
      setBlockedDates([...blockedDates, newBlockedDate].sort());
      setNewBlockedDate('');
    }
  };

  const removeBlockedDate = (date) => {
    setBlockedDates(blockedDates.filter((d) => d !== date));
  };

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Toggle an hour on a specific date. Cycles between AVAILABLE and BLOCKED.
  // Base state comes from weekly config: if weekly enabled + hour in weekly.slots -> available
  //   else -> closed (need opened_slots to make available)
  // If currently available (either by weekly or by opened_slots) -> block it
  //   - remove from opened_slots if present
  //   - add to blocked_slots
  // If currently blocked (either by blocked_slots or weekly-closed with no override) -> open it
  //   - remove from blocked_slots if present
  //   - if weekly-closed, add to opened_slots
  const isSlotBooked = (date, time) => (bookedSlots[date] || []).includes(time);

  const isSlotAvailable = (date, time) => {
    if (blockedSlots.includes(`${date} ${time}`)) return false;
    if (openedSlots.includes(`${date} ${time}`)) return true;
    const dayKey = String(new Date(date + 'T00:00:00').getDay());
    const cfg = weekly[dayKey];
    if (!cfg || !cfg.enabled) return false;
    return (cfg.slots || []).includes(time);
  };

  const toggleExceptionSlot = (date, time) => {
    const entry = `${date} ${time}`;
    if (isSlotBooked(date, time)) {
      toast.info('Esta hora tiene una clase reservada. Cancélala o reprográmala desde "Reservas" para liberarla.');
      return;
    }
    if (isSlotAvailable(date, time)) {
      // make it blocked
      setOpenedSlots((prev) => prev.filter((e) => e !== entry));
      setBlockedSlots((prev) => (prev.includes(entry) ? prev : [...prev, entry].sort()));
    } else {
      // make it available
      setBlockedSlots((prev) => prev.filter((e) => e !== entry));
      const dayKey = String(new Date(date + 'T00:00:00').getDay());
      const cfg = weekly[dayKey];
      const weeklyHas = cfg && cfg.enabled && (cfg.slots || []).includes(time);
      if (!weeklyHas) {
        setOpenedSlots((prev) => (prev.includes(entry) ? prev : [...prev, entry].sort()));
      }
    }
  };

  const addExceptionDate = () => {
    if (!newExceptionDate) return;
    if (exceptionDates.includes(newExceptionDate)) return;
    setExceptionDates((prev) => [...prev, newExceptionDate].sort());
    setNewExceptionDate('');
  };

  const removeExceptionDate = (date) => {
    // remove all overrides for this date + remove row
    setBlockedSlots((prev) => prev.filter((e) => !e.startsWith(`${date} `)));
    setOpenedSlots((prev) => prev.filter((e) => !e.startsWith(`${date} `)));
    setExceptionDates((prev) => prev.filter((d) => d !== date));
  };

  const weekdayLabel = (date) => {
    const d = new Date(date + 'T00:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/availability`, {
        weekly,
        blocked_dates: blockedDates,
        blocked_slots: blockedSlots,
        opened_slots: openedSlots,
      }, authHeaders);
      toast.success('¡Disponibilidad guardada correctamente!');
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('Sesión expirada. Inicia sesión de nuevo.');
        await logout();
        navigate('/admin/login');
      } else {
        toast.error('Error al guardar la disponibilidad');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F5FA] to-white">
      <Seo fullTitle="Panel de administración" path="/admin" noindex />
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/logo.png" alt="Logo" className="h-12 w-12 rounded-full object-cover" />
            <div>
              <h1 className="font-bold text-[#4A4A4A]">Panel de Administración</h1>
              <p className="text-xs text-gray-500">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-[#7DBDD9] transition-colors hidden sm:block"
            >
              Ver web
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-[#4A4A4A] transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100 w-fit">
          <button
            onClick={() => setActiveTab('availability')}
            data-testid="tab-availability"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'availability' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            <span>Disponibilidad</span>
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            data-testid="tab-bookings"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'bookings' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Inbox className="w-4 h-4" />
            <span>Reservas</span>
          </button>
          <button
            onClick={() => setActiveTab('students')}
            data-testid="tab-students"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'students' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Alumnos</span>
          </button>
          <button
            onClick={() => setActiveTab('packs')}
            data-testid="tab-packs"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'packs' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Ticket className="w-4 h-4" />
            <span>Packs</span>
          </button>
          <button
            onClick={() => setActiveTab('materials')}
            data-testid="tab-materials"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'materials' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Materiales</span>
          </button>
          <button
            onClick={() => setActiveTab('podcast')}
            data-testid="tab-podcast"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'podcast' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Mic className="w-4 h-4" />
            <span>Podcast</span>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            data-testid="tab-calendar"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'calendar' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <CalendarClock className="w-4 h-4" />
            <span>Calendario</span>
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            data-testid="tab-blog"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'blog' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Blog</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            data-testid="tab-reviews"
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'reviews' ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Star className="w-4 h-4" />
            <span>Reseñas</span>
          </button>
        </div>

        {activeTab === 'bookings' && (
          <AdminBookings onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'students' && (
          <AdminStudents onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'packs' && (
          <AdminPacks onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'materials' && (
          <AdminMaterials onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'podcast' && (
          <AdminPodcast onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'calendar' && (
          <AdminCalendar token={token} onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'blog' && (
          <AdminBlog onSessionExpired={handleSessionExpired} />
        )}

        {activeTab === 'reviews' && (
          <AdminReviews />
        )}

        {activeTab === 'availability' && (loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
          </div>
        ) : (
        <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h2 className="text-2xl font-bold text-[#4A4A4A] mb-2">Configura tu disponibilidad semanal</h2>
          <p className="text-gray-600">Activa los días que trabajas y selecciona tus franjas horarias. Los cambios se reflejarán en el calendario de reservas.</p>
        </motion.div>

        {/* Weekly Schedule */}
        <div className="space-y-4 mb-10">
          {DAYS.map(({ key, label }, index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`day-card-${key}`}
              className={`rounded-2xl border-2 p-5 transition-all ${
                weekly[key]?.enabled ? 'bg-white border-[#A8D8EA] shadow-md' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className={`w-5 h-5 ${weekly[key]?.enabled ? 'text-[#7DBDD9]' : 'text-gray-400'}`} />
                  <span className="font-bold text-[#4A4A4A] text-lg">{label}</span>
                </div>
                {/* Toggle Switch */}
                <button
                  onClick={() => toggleDay(key)}
                  data-testid={`day-toggle-${key}`}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                    weekly[key]?.enabled ? 'bg-[#A8D8EA]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${
                      weekly[key]?.enabled ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {weekly[key]?.enabled && (
                <div className="flex flex-wrap gap-2">
                  {ALL_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => toggleSlot(key, slot)}
                      data-testid={`slot-${key}-${slot}`}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        weekly[key]?.slots.includes(slot)
                          ? 'bg-[#7DBDD9] text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Clock className="w-3 h-3" />
                      <span>{slot}</span>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Blocked Dates */}
        <div className="bg-white rounded-2xl border-2 border-[#F5A6A4]/40 p-6 mb-10">
          <h3 className="text-lg font-bold text-[#4A4A4A] mb-2">Bloquear fechas concretas</h3>
          <p className="text-gray-600 text-sm mb-4">Añade días específicos en los que NO estarás disponible (vacaciones, festivos, etc.)</p>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="date"
              value={newBlockedDate}
              onChange={(e) => setNewBlockedDate(e.target.value)}
              data-testid="blocked-date-input"
              min={new Date().toISOString().split('T')[0]}
              className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#F5A6A4] focus:ring-2 focus:ring-[#F5A6A4]/30 outline-none"
            />
            <button
              onClick={addBlockedDate}
              data-testid="add-blocked-date-btn"
              className="flex items-center space-x-2 px-4 py-2.5 bg-[#F5A6A4] text-white rounded-xl font-medium hover:bg-[#E38987] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Bloquear</span>
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {blockedDates.length === 0 && (
              <p className="text-gray-400 text-sm">No hay fechas bloqueadas</p>
            )}
            {blockedDates.map((date) => (
              <span
                key={date}
                data-testid={`blocked-date-${date}`}
                className="flex items-center space-x-2 px-3 py-1.5 bg-[#FFE5E4] text-[#E38987] rounded-lg text-sm font-medium"
              >
                <span>{date}</span>
                <button onClick={() => removeBlockedDate(date)} className="hover:text-red-600">
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Day-specific exceptions */}
        <div className="bg-white rounded-2xl border-2 border-[#A8D8EA]/40 p-6 mb-10">
          <h3 className="text-lg font-bold text-[#4A4A4A] mb-2">Excepciones por día</h3>
          <p className="text-gray-600 text-sm mb-5">
            Añade un día específico y ajusta las horas: <strong>abre</strong> las que quieras trabajar aunque no sean de tu horario habitual,
            o <strong>bloquea</strong> las que no puedas atender ese día concreto. Ideal para horarios rotativos.
          </p>

          {/* Add new day */}
          <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
            <input
              type="date"
              value={newExceptionDate}
              onChange={(e) => setNewExceptionDate(e.target.value)}
              data-testid="new-exception-date-input"
              min={new Date().toISOString().split('T')[0]}
              className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
            />
            <button
              onClick={addExceptionDate}
              disabled={!newExceptionDate || exceptionDates.includes(newExceptionDate)}
              data-testid="add-exception-date-btn"
              className="flex items-center space-x-2 px-4 py-2.5 bg-[#7DBDD9] text-white rounded-xl font-medium hover:bg-[#5DA6C9] transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              <span>Añadir día</span>
            </button>
          </div>

          {/* Rows per date */}
          {exceptionDates.length === 0 ? (
            <p className="text-gray-400 text-sm italic text-center py-6">No hay excepciones. Añade un día para empezar.</p>
          ) : (
            <div className="space-y-4" data-testid="exception-dates-list">
              {exceptionDates.map((date) => (
                <div key={date} className="border border-gray-200 rounded-xl p-4" data-testid={`exception-row-${date}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-bold text-[#4A4A4A] capitalize">{weekdayLabel(date)}</p>
                      <p className="text-xs text-gray-500">Verde = disponible · Rojo = bloqueada · Azul = reservada por un alumno</p>
                    </div>
                    <button
                      onClick={() => removeExceptionDate(date)}
                      data-testid={`remove-exception-${date}`}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      title="Eliminar este día"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2">
                    {ALL_SLOTS.map((t) => {
                      const booked = isSlotBooked(date, t);
                      const available = !booked && isSlotAvailable(date, t);
                      return (
                        <button
                          key={t}
                          onClick={() => toggleExceptionSlot(date, t)}
                          data-testid={`exception-slot-${date}-${t}`}
                          title={booked ? 'Clase reservada por un alumno' : undefined}
                          className={`py-1.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                            booked
                              ? 'bg-sky-100 text-sky-800 border border-sky-400 cursor-not-allowed'
                              : available
                                ? 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                                : 'bg-red-50 text-red-700 border border-red-300 line-through hover:bg-red-100'
                          }`}
                        >
                          {booked ? <CalendarCheck className="w-3 h-3" /> : available ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="sticky bottom-4">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-availability-btn"
            className="w-full py-4 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all disabled:opacity-60 flex items-center justify-center space-x-2"
          >
            {saving ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Guardando...</span></> : <><Save className="w-5 h-5" /><span>Guardar Disponibilidad</span></>}
          </button>
        </div>
        </>
        ))}
      </main>
    </div>
  );
}
